import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  FiUsers,
  FiBriefcase,
  FiCreditCard,
  FiSettings,
  FiBarChart,
  FiShield,
  FiFileText,
  FiHome,
  FiActivity,
  FiStar,
} from 'react-icons/fi';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { useThemeClasses } from '../hooks/useThemeClasses';

const AdminSidebar = () => {
  const tc = useThemeClasses();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    canAccessABM,
    canManageUsers,
    canManageCompanies,
    canManagePlans,
    canManageSystem,
    getPlanName,
    getABMAccessLevel,
    isSuperAdmin,
  } = useAdminPermissions();

  const accessLevel = getABMAccessLevel();

  const sections = [
    {
      label: 'Resumen',
      items: [
        {
          id: 'dashboard',
          label: 'Inicio',
          icon: FiBarChart,
          path: '/admin/dashboard',
          visible: canAccessABM(),
        },
      ],
    },
    {
      label: 'Gestión',
      items: [
        {
          id: 'usuarios',
          label: 'Usuarios',
          icon: FiUsers,
          path: '/admin/users',
          visible: canManageUsers(),
        },
        {
          id: 'empresas',
          label: 'Empresas',
          icon: FiBriefcase,
          path: '/admin/companies',
          visible: canManageCompanies(),
        },
        {
          id: 'planes',
          label: 'Planes',
          icon: FiCreditCard,
          path: '/admin/plans',
          visible: canManagePlans(),
        },
      ],
    },
    {
      label: 'Plataforma',
      items: [
        {
          id: 'beta',
          label: 'Programa beta',
          icon: FiStar,
          path: '/admin/beta',
          visible: canManageSystem(),
        },
        {
          id: 'actividad',
          label: 'Actividad',
          icon: FiActivity,
          path: '/admin/activity',
          visible: canManageSystem(),
        },
        {
          id: 'sistema',
          label: 'Sistema',
          icon: FiSettings,
          path: '/admin/system',
          visible: canManageSystem(),
        },
        {
          id: 'fiscal',
          label: 'Parametría fiscal',
          icon: FiFileText,
          path: '/admin/fiscal',
          visible: canManageSystem() && isSuperAdmin(),
        },
      ],
    },
  ];

  if (!canAccessABM()) {
    return (
      <div className={`w-64 h-full min-h-full border-r flex flex-col ${tc.box}`}>
        <div className={`p-5 border-b ${tc.divide}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#1F80FF] rounded-xl flex items-center justify-center">
              <FiShield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className={`text-sm font-semibold ${tc.textPri}`}>Administración</h2>
              <p className={`text-[11px] ${tc.textMuted}`}>Acceso restringido</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          <p className={`text-sm mb-4 ${tc.textSec}`}>
            Tu plan no incluye el panel de administración.
          </p>
          <Link to="/planes" className={`inline-flex px-3 py-2 rounded-lg text-sm ${tc.btnPrimary}`}>
            Ver planes
          </Link>
        </div>
      </div>
    );
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div
      className={`w-64 h-full min-h-full border-r flex flex-col ${
        tc.modoOscuro ? 'bg-gray-950 border-gray-800' : 'bg-white border-gray-200'
      }`}
    >
      <div className={`p-5 border-b ${tc.divide}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-gradient-to-br from-[#1F80FF] to-[#004AAD] rounded-xl flex items-center justify-center shadow-sm">
            <FiShield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className={`text-sm font-semibold ${tc.textPri}`}>Panel admin</h2>
            <p className={`text-[11px] ${tc.textMuted}`}>
              {accessLevel === 'full' ? 'Plataforma completa' : 'Tu organización'}
            </p>
          </div>
        </div>
        <div className={`rounded-xl px-3 py-2 border ${tc.section}`}>
          <p className={`text-[10px] uppercase tracking-wide font-semibold ${tc.textMuted}`}>Plan</p>
          <p className={`text-sm font-medium truncate ${tc.textPri}`}>{getPlanName()}</p>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
        {sections.map((section) => {
          const items = section.items.filter((i) => i.visible);
          if (!items.length) return null;
          return (
            <div key={section.label}>
              <p className={`px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider ${tc.textMuted}`}>
                {section.label}
              </p>
              <div className="space-y-0.5">
                {items.map((item) => {
                  const active = isActive(item.path);
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                        active
                          ? 'bg-[#1F80FF] text-white shadow-sm shadow-[#1F80FF]/20'
                          : tc.modoOscuro
                            ? 'text-gray-300 hover:bg-gray-900 hover:text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="font-medium truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className={`p-3 border-t ${tc.divide}`}>
        <Link
          to="/inicio"
          className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium border ${
            tc.modoOscuro
              ? 'border-gray-800 text-gray-300 hover:bg-gray-900'
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FiHome className="w-4 h-4" />
          Volver al producto
        </Link>
      </div>
    </div>
  );
};

export default AdminSidebar;
